import React, { createContext, useEffect, useState } from "react"

import { getAuthenticatedUser } from "@services/auth/auth"
import { load, remove, save } from "@services/local-storage"

export const AuthContext = createContext({})
AuthContext.displayName = "AuthContext"

export const AuthProvider = ({ children }) => {
  const userLoaded = load("user")

  const emailVerified = userLoaded?.is_email_verified || false
  const [user, setUser] = useState(userLoaded)
  const [isAuthenticated, setIsAuthenticated] = useState(!!userLoaded)
  const [isEmailVerified, setIsEmailVerified] = useState(emailVerified)
  const [hasCardCode, setHasCardCode] = useState(!!userLoaded?.CardCode)

  // Function to fetch and refresh user data
  const refreshUser = async () => {
    try {
      const data = await getAuthenticatedUser()
      if (data?.errors) {
        setUser(null)
        setIsAuthenticated(false)
        setIsEmailVerified(false)
        setHasCardCode(false)
        remove("token")
        remove("user")
      } else {
        setUser(data)
        setIsAuthenticated(true)
        setIsEmailVerified(data.is_email_verified || false)
        setHasCardCode(!!data.CardCode)
        save("user", data)
      }
    } catch (error) {
      setUser(null)
      setIsAuthenticated(false)
      setIsEmailVerified(false)
      setHasCardCode(false)
      remove("token")
      remove("user")
      console.error("Failed to refresh user data:", error)
    }
  }

  useEffect(() => {
    refreshUser()
  }, [])

  const authenticate = ({ user, token }) => {
    setUser(user)
    setIsAuthenticated(true)
    setIsEmailVerified(user.is_email_verified || false)
    setHasCardCode(!!user.CardCode)
    save("user", user)
    save("token", token)
  }

  const logout = () => {
    setUser(null)
    setIsAuthenticated(false)
    setIsEmailVerified(false)
    setHasCardCode(false)
    remove("token")
    remove("user")
  }

  const updateVerifyEmail = () => {
    setIsEmailVerified(true)
    const updatedUser = { ...user, is_email_verified: true }
    setUser(updatedUser)
    save("user", updatedUser)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        authenticate,
        logout,
        isAuthenticated,
        isEmailVerified,
        hasCardCode,
        updateVerifyEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
